import { useCallback } from "react";
import { useHubSpotAdapterAxiosInstance } from "./useHubSpotAdapterAxiosInstance.ts";
import { useWithLoading } from "../UI/Loading/useWithLoading.ts";
import {
  SchedulerCloserDTO,
  SchedulerExcludedCloserDTO,
} from "./useGetSchedulerClosers.ts";
import { CloserStatsDTO } from "./useGetSchedulerCloserStats.ts";

export type RankedCloserDTO = { closerId: string; name: string; cr: number };

export type CloserRankingDTO = {
  position: number | null;
  closers: RankedCloserDTO[];
  teamCr: number | null;
};

export type NextTierDTO = { tier: string; minCr: number; dealsNeeded: number };

export type CrHistoryPointDTO = {
  day: string;
  won: number;
  callsDone: number;
  cr: number;
  tier: string;
  capacity: number | null;
  blocked: boolean;
  inTrial: boolean;
  bookableBy: string | null;
  source: "job" | "backfill";
};

export type TeamCrPointDTO = { day: string; cr: number | null };

export type CallHeatmapCellDTO = {
  weekday: number;
  hour: number;
  calls: number;
};

export type CloserDetailDTO = {
  fromDay: string;
  dataFromDay: string;
  toDay: string;
  crWindowDays: number;
  trialDays: number;
  closer: SchedulerCloserDTO | null;
  excluded: SchedulerExcludedCloserDTO | null;
  ranking: CloserRankingDTO;
  nextTier: NextTierDTO | null;
  crHistory: CrHistoryPointDTO[];
  teamCrHistory: TeamCrPointDTO[];
  stats: CloserStatsDTO | null;
  callHeatmap: CallHeatmapCellDTO[];
};

export function useGetSchedulerCloserDetail() {
  const axiosInstance = useHubSpotAdapterAxiosInstance();
  const { withLoading, loading } = useWithLoading<CloserDetailDTO>();

  const getDetailCall = useCallback(
    async ({
      closerId,
      fromDay,
      toDay,
    }: {
      closerId: string;
      fromDay: string;
      toDay: string;
    }) => {
      const { data } = await axiosInstance.get<CloserDetailDTO>(
        `/closer-availability/scheduler/closers/${encodeURIComponent(closerId)}?fromDay=${encodeURIComponent(fromDay)}&toDay=${encodeURIComponent(toDay)}`,
      );

      if (!data?.ranking || !Array.isArray(data?.crHistory)) {
        console.error("Unexpected closer detail response:", data);
        throw new Error("Unexpected response format from HubSpot adapter");
      }

      return data;
    },
    [axiosInstance],
  );

  const getSchedulerCloserDetail = useCallback(
    async (query: { closerId: string; fromDay: string; toDay: string }) => {
      return await withLoading(() => getDetailCall(query));
    },
    [withLoading, getDetailCall],
  );

  return {
    getSchedulerCloserDetail,
    loading,
  };
}
